;(function () {
  class EWGRecommendedProducts extends HTMLElement {
    constructor() {
      super()
      this.handleReportReady = this.handleReportReady.bind(this)
      this.handleUnavailable = this.handleUnavailable.bind(this)
      this.handleClick = this.handleClick.bind(this)
    }

    connectedCallback() {
      this.sliderHost = this.querySelector('[data-products-slider]')
      this.sliderTemplate = this.querySelector('[data-slider-template]')
      this.productTemplate = this.querySelector('[data-product-template]')
      this.fallbackProducts = this.readFallbackProducts()
      this.addEventListener('click', this.handleClick)
      document.addEventListener('wd:ewg-ready', this.handleReportReady)
      document.addEventListener('wd:ewg-empty', this.handleUnavailable)
      document.addEventListener('wd:ewg-error', this.handleUnavailable)

      const store = window.WDEwgReportStore
      if (typeof store?.init === 'function') {
        store.init().then(state => this.applyProducts(state))
        return
      }
      this.handleUnavailable()
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick)
      document.removeEventListener('wd:ewg-ready', this.handleReportReady)
      document.removeEventListener('wd:ewg-empty', this.handleUnavailable)
      document.removeEventListener('wd:ewg-error', this.handleUnavailable)
    }

    readFallbackProducts() {
      const node = this.querySelector('[data-fallback-products]')
      if (!node) return []
      try {
        const parsed = JSON.parse(node.textContent)
        return Array.isArray(parsed) ? parsed.filter(Boolean) : []
      } catch {
        return []
      }
    }

    applyProducts(state) {
      const products = state?.recommendedProducts || state?.information?.recommendedProducts
      if (Array.isArray(products) && products.length) {
        this.renderProducts(products)
        return
      }
      this.handleUnavailable()
    }

    handleReportReady(event) {
      this.applyProducts(event.detail)
    }

    handleClick(event) {
      const checkout = event.target.closest('[data-checkout]')
      if (!checkout || !this.contains(checkout)) return

      if (!checkout.getAttribute('href') || checkout.getAttribute('href') === '#') {
        event.preventDefault()
        return
      }

      if (typeof window.WDSendEvent === 'function') {
        window.WDSendEvent('wd_buy_now', {
          event_source: window.location.pathname,
          event_category: 'check-your-tap-water',
          event_action: checkout.dataset.eventSku || ''
        })
      }
    }

    handleUnavailable() {
      if (this.fallbackProducts.length) this.renderProducts(this.fallbackProducts)
    }

    renderProducts(products) {
      if (!this.sliderHost || !this.sliderTemplate || !this.productTemplate || !Array.isArray(products)) return

      const visibleProducts = products.slice(0, 3)
      const renderKey = JSON.stringify(visibleProducts)
      if (renderKey === this.renderedKey) return
      this.renderedKey = renderKey

      const sliderFragment = this.sliderTemplate.content.cloneNode(true)
      const list = sliderFragment.querySelector('.splide__list')
      const priceFormatters = {}

      visibleProducts.forEach((product, index) => {
        const cardFragment = this.productTemplate.content.cloneNode(true)
        const image = cardFragment.querySelector('[data-product-image]')
        const productLinks = cardFragment.querySelectorAll('[data-product-link]')
        const checkout = cardFragment.querySelector('[data-checkout]')
        const recommendation = cardFragment.querySelector('[data-recommendation]')
        const codeButton = cardFragment.querySelector('[data-product-code]')
        const addToCart = cardFragment.querySelector('[data-add-to-cart]')
        const productTitle = product.product_title || product.title || ''
        const productImage = product.product_image || product.image || product.imageUrl || product.featuredImage
        const productUrl = this.isSafeHref(product.product_url || product.url)
          ? (product.product_url || product.url).trim()
          : '#'
        const productType = index === 2 ? 'Countertop' : 'Undersink'
        const currencyCode = product.currency_code || 'USD'
        const priceFormatter =
          priceFormatters[currencyCode] ||
          (priceFormatters[currencyCode] = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }))
        const discountPrice = product.discount_price ?? product.price ?? ''
        const originalPrice = product.original_price ?? product.compareAtPrice ?? product.compare_at_price ?? ''
        const discountPriceNumber = Number(discountPrice)
        const originalPriceNumber = Number(originalPrice)
        const displayPrice =
          discountPrice !== '' && Number.isFinite(discountPriceNumber)
            ? priceFormatter.format(discountPriceNumber)
            : discountPrice
        const hasDiscount =
          Number.isFinite(originalPriceNumber) &&
          Number.isFinite(discountPriceNumber) &&
          originalPriceNumber > discountPriceNumber
        const displayOriginalPrice = hasDiscount ? priceFormatter.format(originalPriceNumber) : ''
        const savingValue = product.saving_display || product.saved_price || ''
        const savingNumber = Number(savingValue)
        const displaySaving =
          hasDiscount && savingValue !== '' && Number.isFinite(savingNumber) && savingNumber > 0
            ? priceFormatter.format(savingNumber).replace(/\.00$/, '')
            : ''
        const saleBadgeText = hasDiscount
          ? product.saleBadge || product.discountBadge || (displaySaving ? `${displaySaving} Off` : '')
          : ''
        const productFeatures = Array.isArray(product.product_highlights)
          ? product.product_highlights.map(item => String(item).trim()).filter(Boolean)
          : []
        const recommendationLabel = index === 0 ? 'Best Match' : index === 2 ? 'Easy Setup' : ''
        const recommendationType = index === 2 ? 'easy' : 'best'

        if (productImage) {
          image.src =
            typeof productImage === 'string' && productImage.includes('cdn.shopify.com')
              ? `${productImage}${productImage.includes('?') ? '&' : '?'}width=500`
              : productImage
        } else {
          image.hidden = true
        }
        image.alt = productTitle
        productLinks.forEach(link => {
          link.href = productUrl
          link.setAttribute('aria-label', `View ${productTitle || 'recommended product'}`)
        })

        const saleBadge = cardFragment.querySelector('[data-sale-badge]')
        saleBadge.textContent = saleBadgeText
        saleBadge.hidden = !saleBadgeText

        const type = cardFragment.querySelector('[data-product-type]')
        type.textContent = productType
        type.hidden = !productType

        cardFragment.querySelector('[data-product-title]').textContent = productTitle
        cardFragment.querySelector('[data-product-price]').textContent = displayPrice
        const comparePrice = cardFragment.querySelector('[data-product-compare-price]')
        comparePrice.textContent = displayOriginalPrice
        comparePrice.hidden = !displayOriginalPrice

        const features = cardFragment.querySelector('[data-product-features]')
        productFeatures.forEach(feature => {
          const item = document.createElement('li')
          item.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
              <circle cx="6" cy="6" r="6" fill="#000"></circle>
              <path stroke="#fff" stroke-width="1.2" stroke-linejoin="bevel" d="M3.333 5.628 5.337 8l3.33-4"></path>
            </svg>
            <span></span>
          `
          item.querySelector('span').textContent = feature
          features.appendChild(item)
        })

        if (recommendationLabel) {
          recommendation.hidden = false
          recommendation.classList.add(`ewg-recommended-product__recommendation--${recommendationType}`)
          recommendation.querySelector('[data-recommendation-label]').textContent = recommendationLabel
          recommendation.querySelector('[data-recommendation-icon]').src =
            recommendationType === 'easy' ? this.dataset.easySetupIcon : this.dataset.bestMatchIcon
        }

        const discountCode = product.discount_code || product.discountCode || ''
        if (discountCode) {
          codeButton.dataset.value = discountCode
          codeButton.querySelector('[data-product-code-text]').textContent = discountCode
        } else {
          codeButton.classList.add('is-empty')
          codeButton.disabled = true
        }

        const variantId = String(product.variant_id || product.variantId || '')
          .split('/')
          .pop()
        const available = product.available_for_sale ?? product.available ?? true
        const inventoryQuantity = product.inventory_quantity ?? product.inventoryQuantity
        const inventoryPolicy = product.inventory_policy || product.inventoryPolicy || ''
        const inventoryManagement = product.inventory_management || product.inventoryManagement || ''
        addToCart.dataset.atcId = variantId
        addToCart.dataset.atcAvailable = String(available)
        addToCart.dataset.atcInventory =
          inventoryQuantity === null || inventoryQuantity === undefined ? '' : String(inventoryQuantity)
        addToCart.dataset.atcInventoryPolicy = inventoryPolicy
        addToCart.dataset.atcInventoryManagement = inventoryManagement
        addToCart.dataset.eventSku = product.sku || ''
        addToCart.setAttribute('aria-label', `Add to Cart ${productTitle}`)
        if (checkout) {
          checkout.href = variantId ? `/cart/${variantId}:1` : '#'
          checkout.dataset.eventSku = product.sku || ''
          checkout.setAttribute('aria-label', `Checkout ${productTitle}`)
        }

        const isSoldOut =
          product.is_sold_out === true ||
          !variantId ||
          available === false ||
          available === 'false' ||
          (inventoryManagement && inventoryPolicy !== 'continue' && Number(inventoryQuantity) <= 0)

        if (isSoldOut) {
          addToCart.disabled = true
          addToCart.ariaDisabled = 'true'
          addToCart.classList.add('component-atc-button--sold-out')
          addToCart.querySelector('.component-atc-button__text').textContent = 'Sold Out'
          addToCart.setAttribute('aria-label', `Sold Out ${productTitle}`)
          const buttons = cardFragment.querySelector('.ewg-recommended-product__buttons')
          if (buttons) buttons.classList.add('ewg-recommended-product__buttons--sold-out')
          if (checkout) checkout.remove()
        }

        list.appendChild(cardFragment)
      })

      this.sliderHost.replaceChildren(sliderFragment)
      this.sliderHost.ariaBusy = 'false'
      window.WDEwgRecommendedProducts = visibleProducts
      document.dispatchEvent(new CustomEvent('wd:ewg-products-ready', { detail: { products: visibleProducts } }))
    }

    isSafeHref(url) {
      if (typeof url !== 'string' || !url.trim()) return false
      const value = url.trim()
      if (value.startsWith('/') && !value.startsWith('//') && !value.includes('\\')) return true
      try {
        const parsed = new URL(value, window.location.origin)
        return (parsed.protocol === 'https:' || parsed.protocol === 'http:') && parsed.origin === window.location.origin
      } catch {
        return false
      }
    }
  }

  if (!customElements.get('ewg-recommended-products')) {
    customElements.define('ewg-recommended-products', EWGRecommendedProducts)
  }
})()
